import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TypePagesEditor } from "./TypePagesEditor";
import { PageFieldsEditor } from "./PageFieldsEditor";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const HERO = [["eyebrow", "Eyebrow", Input], ["headline", "Headline", Input], ["accent", "Gold accent line", Input], ["subhead", "Paragraph", Textarea]];

export default function AdminSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [hero, setHero] = useState(null);
  const [desc, setDesc] = useState(null);
  const [hiddenFields, setHiddenFields] = useState([]);
  const [sectionLabels, setSectionLabels] = useState({});
  const [sectionOrder, setSectionOrder] = useState([]);
  const [exploreThemes, setExploreThemes] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (data) { setHero(data.hero); setDesc(data.type_pages || {}); setHiddenFields(data.hidden_fields || []); setSectionLabels(data.section_labels || {}); setSectionOrder(data.section_order || []); setExploreThemes(!!data.explore_themes); } }, [data]);
  if (!hero || !desc) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { hero, type_pages: desc, hidden_fields: hiddenFields, section_labels: sectionLabels, section_order: sectionOrder, explore_themes: exploreThemes });
      toast.success("Site content saved.");
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["types"] });
      qc.invalidateQueries({ queryKey: ["type"] });
      qc.invalidateQueries({ queryKey: ["question"] });
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <section className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-serif text-2xl font-semibold text-foreground">Homepage hero</h2>
        {HERO.map(([k, l, C]) => (
          <label key={k} className="block">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{l}</span>
            <C className="mt-1" rows={3} value={hero[k]} onChange={(e) => setHero({ ...hero, [k]: e.target.value })} data-testid={`settings-hero-${k}`} />
          </label>
        ))}
      </section>

      <section className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-5" data-testid="settings-explore-themes">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-foreground">Explore by theme</h2>
          <p className="mt-1 text-sm text-muted-foreground">Shows "By Theme" in the Explore menu and footer, the theme pages, and the Theme filter on Explore. When off, theme pages redirect to Explore.</p>
        </div>
        <Switch checked={exploreThemes} onCheckedChange={setExploreThemes} data-testid="settings-explore-themes-switch" aria-label="Show Explore by theme" />
      </section>

      <PageFieldsEditor fields={data.page_fields || []} hidden={hiddenFields} onChange={setHiddenFields}
        labels={sectionLabels} defaults={data.default_labels || {}} onLabels={setSectionLabels} order={sectionOrder} onOrder={setSectionOrder} />

      <TypePagesEditor pages={desc} onChange={setDesc} />

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="rounded-full bg-primary text-primary-foreground" data-testid="settings-save">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save site content
        </Button>
      </div>
    </div>
  );
}
